import { Layout, Divider, Button } from "antd";
import { Header, Content } from "antd/es/layout/layout";
import Sider from "antd/es/layout/Sider";
import { useState, useEffect, useLayoutEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSecureService } from "../store/service";
import { useAtom } from "jotai";
import { userAtom } from "../store/atoms";
import { Role } from "../constants/roles";
import styles from "./mainLayout.module.scss";
import AuthChecker from "../components/AuthChecker/AuthChecker";
import SidebarContent from "../components/Sidebar/SidebarContent";
import UserSection from "../components/UserSection/UserSection";
import MobileDrawer from "../components/MobileDrawer/MobileDrawer";

const getSavedSidebarCollapsed = (fallback: boolean) => {
  try {
    const saved = localStorage.getItem('sidebarCollapsed');
    if (saved !== null) {
      return JSON.parse(saved);
    }
  } catch {
    // ignore
  }
  return fallback;
};

const MainLayout = ({
  children,
  title,
  icon,
  headerActions,
  headerLeft,
  topBarActions,
  autoCollapse,
  forceInitialCollapse
}: {
  children: React.ReactNode,
  title?: string,
  icon?: React.ReactNode,
  headerActions?: React.ReactNode,
  headerLeft?: React.ReactNode,
  /** Üst bar (isim yanı) için aksiyonlar, örn. New Project butonu */
  topBarActions?: React.ReactNode,
  autoCollapse?: boolean,
  forceInitialCollapse?: boolean
}) => {
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const { doLogout } = useSecureService();
  const navigate = useNavigate();
  const [user] = useAtom(userAtom);

  const location = useLocation();
  const path = location.pathname;
  const hasTitleText = Boolean(title && String(title).trim());
  const hasContentHeader = hasTitleText || icon;
  const showPageContentHeader = hasContentHeader || headerActions;
  const useProjectsTitleUnderline =
    path.startsWith("/internal-projects") ||
    path.startsWith("/external-projects") ||
    path.startsWith("/projects");
  const isDashboardPage =
    path === "/dashboard" || path === "/dashboard/";

  // Mobile drawer state — route değişince sıfırlanır; Drawer sadece mobilde mount (ilk paint tutarsızlığı önlenir)
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= 800
  );
  
  // Sidebar collapse state - forceInitialCollapse varsa başlangıçta kapalı yap, yoksa localStorage'dan al veya default değeri kullan
  const getInitialCollapsedState = () => {
    if (forceInitialCollapse) {
      return true;
    }
    return getSavedSidebarCollapsed(autoCollapse || false);
  };
  
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(getInitialCollapsedState);
  /** Ana içerik soldan sağa giriş; sidebar bu state’ten etkilenmez */
  const [mainStageReady, setMainStageReady] = useState(false);

  /* useLayoutEffect aynı paint öncesi tetiklenir → “enter” karesi boyanmaz. Paint’ten sonra + çift rAF ile giriş görünür. */
  useEffect(() => {
    let r2 = 0;
    const r1 = requestAnimationFrame(() => {
      r2 = requestAnimationFrame(() => {
        setMainStageReady(true);
      });
    });
    return () => {
      cancelAnimationFrame(r1);
      cancelAnimationFrame(r2);
    };
  }, []);

  // Sidebar collapse handler
  const handleSidebarCollapse = (collapsed: boolean) => {
    setIsSidebarCollapsed(collapsed);
    localStorage.setItem('sidebarCollapsed', JSON.stringify(collapsed));
    document.documentElement.classList.toggle('sidebar-collapsed', collapsed);
  };

  // forceInitialCollapse değiştiğinde sadece başlangıçta sidebar'ı kapalı yap
  useLayoutEffect(() => {
    if (forceInitialCollapse) {
      setIsSidebarCollapsed(true);
      localStorage.setItem('sidebarCollapsed', JSON.stringify(true));
      document.documentElement.classList.add('sidebar-collapsed');
    }
  }, [forceInitialCollapse]);

  useLayoutEffect(() => {
    document.documentElement.classList.toggle('sidebar-collapsed', isSidebarCollapsed);
  }, [isSidebarCollapsed, location.pathname]);

  // Guest: sadece external alan; internal ve settings yok. Anon: eski korumalar.
  useEffect(() => {
    const currentPath = location.pathname;
    const isSettings = currentPath === '/settings' || currentPath.startsWith('/settings/');
    const isDashboard = currentPath === '/dashboard' || currentPath === '/dashboard/';
    const isInternal = currentPath === '/internal-projects' || currentPath.startsWith('/internal-projects/');
    if (user?.role === Role.Guest) {
      if (isSettings) {
        navigate('/external-projects');
        return;
      }
      if (isInternal) {
        navigate('/external-projects');
        return;
      }
    }
    if (!user && (isSettings || isDashboard)) {
      navigate('/internal-projects');
    }
    if (currentPath === '/') {
      if (!user) {
        navigate('/internal-projects');
      } else if (user.role === Role.Guest) {
        navigate('/external-projects');
      } else {
        navigate('/dashboard');
      }
    }
  }, [user, location.pathname, navigate]);

  // Menu click handler
  const handleMenuClick = ({ key }: { key: string }) => {
    if (key === 'logout') {
      doLogout();
      navigate('/login');
    } else if (key === 'login') {
      navigate('/login');
    } else {
      setSelectedKeys([key]);
      
      // Mobile drawer'ı kapat
      if (isMobile) {
        setIsMobileDrawerOpen(false);
      }
      
      navigate(key);
    }
  };

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 800);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  /* Masaüstüne geçince drawer açık kalmasın — sider + drawer çakışması */
  useEffect(() => {
    if (!isMobile) {
      setIsMobileDrawerOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    setIsMobileDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const currentPath = location.pathname;
    if (currentPath.startsWith('/internal-projects') || currentPath.startsWith('/projects') || currentPath.startsWith('/snps')) {
      setSelectedKeys(['/internal-projects']);
    } else if (currentPath.startsWith('/external-projects')) {
      setSelectedKeys(['/external-projects']);
    } else {
      setSelectedKeys([currentPath]);
    }
  }, [location.pathname]);



  return (
    <AuthChecker
      isMobile={isMobile}
      selectedKeys={selectedKeys}
      onMenuClick={handleMenuClick}
      onMobileDrawerOpen={() => setIsMobileDrawerOpen(true)}
    >
      <Layout
        data-app-layout-shell
        className={`${styles.layout} ${styles.ready} ${styles.mainStage} ${mainStageReady ? styles.mainStageReady : ''}`}
      >
        {/* Desktop Sider */}
        {!isMobile && (
          <Sider 
            width="10%" 
            collapsedWidth={68}
            collapsed={isSidebarCollapsed}
            className={styles.sider}
          >
            <SidebarContent 
              selectedKeys={selectedKeys}
              onMenuClick={({ key }) => {
                if (key === 'logout') {
                  doLogout();
                  navigate('/login');
                } else if (key === 'login') {
                  navigate('/login');
                } else {
                  navigate(key);
                }
              }}
              isCollapsed={isSidebarCollapsed}
              onToggleCollapse={() => handleSidebarCollapse(!isSidebarCollapsed)}
            />
          </Sider>
        )}
        
        <Layout className={`${styles.mainColumn} ${isSidebarCollapsed ? styles.layoutCollapsed : ''}`}>
          <Header className={styles.header}>
            {/* Mobile Menu Button */}
            {isMobile && (
              <Button 
                type="primary" 
                icon={<img src="/layout.svg" alt="menu" width={20} height={20} />}
                onClick={() => setIsMobileDrawerOpen(true)}
                className={styles.mobileMenuButton}
              />
            )}
            {headerLeft ? (
              <div className={styles.headerLeft}>
                {headerLeft}
              </div>
            ) : null}
            {topBarActions ? (
              <div className={styles.topBarActions}>
                {topBarActions}
              </div>
            ) : null}
            <div className={styles.userContainer}>
              <UserSection
                isMobile={isMobile}
                hideUsernameWhenTopBarActions={Boolean(topBarActions)}
                onMobileDrawerClose={() => setIsMobileDrawerOpen(false)}
              />
            </div>
          </Header>
          <Content className={styles.content}>
            <div className={styles.contentContainer}>
              {showPageContentHeader ? (
                <div
                  className={`${styles.contentHeader}${
                    useProjectsTitleUnderline && hasContentHeader ? ` ${styles.contentHeaderProjectsUnderline}` : ""
                  }${isDashboardPage && hasTitleText ? ` ${styles.contentHeaderDashboard}` : ""}`}
                >
                  <div className={styles.contentHeaderTop}>
                    {hasContentHeader ? (
                      <h1>
                        {icon}
                        {hasTitleText ? <span> {title}</span> : null}
                      </h1>
                    ) : null}
                    {headerActions ? (
                      <div className={styles.contentHeaderActions}>{headerActions}</div>
                    ) : null}
                  </div>
                  {hasTitleText && !useProjectsTitleUnderline ? (
                    <Divider type="horizontal" className={styles.divider} />
                  ) : null}
                </div>
              ) : null}
              <div className={styles.contentBody} data-app-scroll-lock>
                {children}
              </div>
            </div>
          </Content>
        </Layout>
        
        {/* Mobile Drawer — yalnızca mobilde DOM'da; sayfa/route yükünde kapalı */}
        {isMobile ? (
          <MobileDrawer
            isOpen={isMobileDrawerOpen}
            onClose={() => setIsMobileDrawerOpen(false)}
            selectedKeys={selectedKeys}
            onMenuClick={handleMenuClick}
          />
        ) : null}
      </Layout>
    </AuthChecker>
  );
};

export default MainLayout;
